import React, { MouseEventHandler, useCallback, useRef } from 'react';

/**
 * Helper function which returns a function, which can be used to pass to an 'onClick' event to automatically stop propagation for said event.
 *
 * @param clickHandler the handler to execute
 * @return a function which takes a MouseEvent, which can be passed to an element handler
 */
export function noPropagationClickHandler(
  clickHandler?: MouseEventHandler
): React.MouseEventHandler {
  // return function which stops propagation of passed MouseEvent
  return (event: React.MouseEvent): void => {
    event.stopPropagation();
    clickHandler?.(event);
  };
}

/**
 * useSingleAndDoubleClick hook for when a component needs to have both a single- and a doubleClick handler.
 * It is possible to pass <code>undefined</code> for both the clickHandler and the doubleClickHandler
 * for components that don't always need both handlers, to avoid unnecessary boilerplate null checks.
 * Regardless of if the handlers are defined or not, the returned handlers will always call <strong><code>stopPropagation</code></strong> on the event.
 *
 * @param clickHandler the function to execute on single click
 * @param doubleClickHandler the function to execute on double click
 * @param clickTimeout maximum number of ms that can be between two consecutive clicks for them to be registered as a 'double click' - default: 250
 * @return an array of 2 elements, with the first being a function for handling single click and the second a function for handling double clicks
 */
export function useSingleAndDoubleClick(
  clickHandler?: MouseEventHandler,
  doubleClickHandler?: MouseEventHandler,
  clickTimeout = 250
): [MouseEventHandler, MouseEventHandler] {
  // Since the DOM always fires the singleClick handler, it's necessary to keep
  // track of if the doubleClick handler was called within a specific timeframe or not.
  const isSingleClick = useRef(false);

  /**
   * Handles a single click event.
   * When both clickHandler and doubleClickHandler is defined, a delay of 250ms is applied, to prevent single- and doubleClick from interfering with each other.<br/>
   *
   * @param event the event to handle.
   */
  const handleClick = useCallback(
    (event: React.MouseEvent): void => {
      if (clickHandler && doubleClickHandler) {
        isSingleClick.current = true;
        // event needs to be persisted, due to it being asynchronous because of the timeout
        event.persist();
        // stopping propagation of the event without using the noPropagationClickHandler
        // because the timeout makes the noPropagationClickHandler useless
        event.stopPropagation();
        // wait for doubleClick for 250ms
        setTimeout(() => {
          // if not doubleClick
          if (isSingleClick.current && clickHandler) {
            clickHandler(event);
          }
        }, clickTimeout);
      } else {
        // simply call the handler if doubleClickHandler is not defined
        noPropagationClickHandler(clickHandler)(event);
      }
    },
    [clickHandler, doubleClickHandler, clickTimeout]
  );

  /**
   * Handles a double click event.
   *
   * @param event the event to handle
   */
  const handleDblClick = useCallback(
    (event: React.MouseEvent): void => {
      isSingleClick.current = false;
      noPropagationClickHandler(doubleClickHandler)(event);
    },
    [doubleClickHandler]
  );

  return [handleClick, handleDblClick];
}
